import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { projectPath } from './projectDeepLink';

// ─── DASHBOARD ───────────────────────────────────────────────────────────────
// Real portfolio data from `GET /dashboard/portfolio-summary` (replaced the 5-project sample-data
// concept preview, 2026-09-18) — nothing here re-derives the backend's progress/go-live/event math.

// Mirrors ProjectLaunch.js's RAG_BADGE_CLASS/RAG_STATUS_VALUES ('Red'/'Amber'/'Green' — the values a
// PM actually sets on Project.ragStatus). 'Unset' is this page's own bucket for a project no one has
// rated yet, not a value the API sends.
const RAG_CONFIG = {
  Green:  { label: 'On Track',  badgeClass: 'badge-green',  dot: 'var(--green)' },
  Amber:  { label: 'At Risk',   badgeClass: 'badge-yellow', dot: 'var(--yellow)' },
  Red:    { label: 'Critical',  badgeClass: 'badge-red',    dot: 'var(--red)' },
  Unset:  { label: 'Not Rated', badgeClass: 'badge-gray',   dot: 'var(--gray)' },
};
const RAG_ORDER = { Red: 0, Amber: 1, Green: 2, Unset: 3 };

// Mirrors PortfolioTimeline.js's STATUS_STYLE, badge only (no bar color needed here).
const STATUS_BADGE = {
  Placeholder: 'badge-violet',
  Startup: 'badge-blue',
  Active: 'badge-blue',
  'Final Acceptance': 'badge-green',
  Converted: 'badge-gray',
  Completed: 'badge-green',
  Archived: 'badge-gray',
};

// Statuses after go-live but before the project closes out (PROJECT_STATUS_VALUES in apps/api's
// project-status.constants.ts). A project here already went live on schedule — a go-live date in the
// past is the expected, successful outcome, not a slip — so the Go-Live tile reads "Live" instead of
// counting days overdue (Jeremy, 2026-09-18; Snohomish is the example that surfaced this).
const POST_GO_LIVE_STATUSES = new Set(['Final Acceptance', 'Converted']);

// Mirrors the backend's PortfolioService.MILESTONE_DELIVERED / ProjectSchedule.js's
// MILESTONE_COMPLETE — a milestone in one of these statuses isn't "overdue" even past its target date.
const MILESTONE_COMPLETE = new Set(['Delivered', 'Accepted', 'Billed', 'Paid']);

const SECTION_LABEL_STYLE = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)', marginBottom: 8,
};
const QUICK_LINK_STYLE = { fontSize: 12, padding: '6px 12px' };

const HEALTH_DIMENSIONS = [
  ['budgetHealth', 'Budget'],
  ['scopeHealth', 'Scope'],
  ['scheduleHealth', 'Schedule'],
  ['clientHealth', 'Client'],
];

// A milestone's link points at /schedule, which 404s when the flag is off (mirrors
// PortfolioTimeline.js's own SCHEDULING_ENABLED gate) — fall back to the project's launch hub instead.
const SCHEDULING_ENABLED = process.env.REACT_APP_SCHEDULING_ENABLED === 'true';

// ─── DATES ───────────────────────────────────────────────────────────────────
// Portfolio-row dates need PortfolioTimeline.js's UTC-getter correction (ADR 0009); calendar events
// don't (CalendarEvent's own contract already serializes a real instant for an internal caller).

export function toDay(value) {
  if (!value) return null;
  const d = new Date(value);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function startOfToday() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function fmtFullDate(value) {
  const d = toDay(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export function daysUntil(value) {
  const d = toDay(value);
  return d ? Math.round((+d - +startOfToday()) / 86_400_000) : null;
}

function fmtCountdown(days) {
  if (days === null) return 'TBD';
  if (days === 0) return 'Today';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `${days}d`;
}

export function ragKey(value) {
  return RAG_CONFIG[value] ? value : 'Unset';
}

function fmtEventDate(event) {
  const d = new Date(event.start);
  const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return event.timeLabel ? `${label} · ${event.timeLabel}` : label;
}

// The overdue milestones behind a project's go-live countdown — the "why" for a tile that just
// shows "35d overdue". Oldest miss first: the milestone that's been overdue longest is the one most
// worth a PM's attention.
function overdueMilestones(p) {
  return (p.milestones ?? [])
    .filter((m) => m.targetDate && !MILESTONE_COMPLETE.has(m.status) && daysUntil(m.targetDate) < 0)
    .sort((a, b) => +new Date(a.targetDate) - +new Date(b.targetDate));
}

function eventLink(event) {
  if (event.type === 'milestone' && !SCHEDULING_ENABLED) return projectPath('/project-launch', event.projectId);
  return event.link;
}

// ─── SMALL PIECES ────────────────────────────────────────────────────────────

function Stat({ value, label, tone, sub }) {
  return (
    <div className={`stat${tone ? ` is-${tone}` : ''}`}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function HealthDot({ rag }) {
  return (
    <span style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      background: RAG_CONFIG[rag].dot, marginRight: 6, flexShrink: 0,
    }} />
  );
}

function ProgressBar({ pct, color }) {
  return (
    <div style={{ background: 'var(--border)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s ease' }} />
    </div>
  );
}

// One thin labeled bar in the three-bar breakdown below. `pct` null (no schedule/milestones/budget to
// measure yet) renders an empty gray bar with `emptyText` instead of a misleading 0%.
function ProgressRow({ label, pct, color, emptyText }) {
  return (
    <div style={{ marginBottom: 5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-mid)', marginBottom: 2 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 700, color: pct === null ? 'var(--text-mid)' : 'var(--text-dark)' }}>
          {pct === null ? emptyText : `${pct}%`}
        </span>
      </div>
      <ProgressBar pct={pct === null ? 0 : Math.min(pct, 100)} color={pct === null ? 'var(--border)' : color} />
    </div>
  );
}

// Green at/under budget, amber a little over, red well over — the one bar of the three with a
// meaningful "too much" direction (Jeremy, 2026-09-18).
function effortColor(pct) {
  if (pct === null) return 'var(--border)';
  if (pct <= 90) return 'var(--green)';
  if (pct <= 110) return 'var(--yellow)';
  return 'var(--red)';
}

/** A horizontal stacked bar of the whole portfolio's RAG mix — the "eye-catching" read at a glance. */
function RagMixBar({ counts, total }) {
  if (!total) return null;
  return (
    <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginTop: 10 }}>
      {['Red', 'Amber', 'Green', 'Unset'].map((k) => counts[k] > 0 && (
        <div key={k} title={`${RAG_CONFIG[k].label}: ${counts[k]}`} style={{ width: `${(counts[k] / total) * 100}%`, background: RAG_CONFIG[k].dot }} />
      ))}
    </div>
  );
}

// ─── PROJECT ROW ─────────────────────────────────────────────────────────────

function ProjectRow({ project, expanded, onToggle }) {
  const p = project;
  const rag = ragKey(p.ragStatus);
  const days = daysUntil(p.goLive?.date);
  const goLiveMark = p.goLive?.source && p.goLive.source !== 'milestone' ? '~' : '';
  const isPostGoLive = POST_GO_LIVE_STATUSES.has(p.projectStatus);
  const overdue = overdueMilestones(p);

  return (
    <div className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${RAG_CONFIG[rag].dot}` }} onClick={onToggle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
            <HealthDot rag={rag} />
            <span style={{ fontFamily: 'Georgia', fontSize: 15, fontWeight: 'bold', color: 'var(--heading)' }}>{p.name}</span>
            {p.state && <span className="badge badge-blue">{p.state}</span>}
            {STATUS_BADGE[p.projectStatus] && <span className={`badge ${STATUS_BADGE[p.projectStatus]}`}>{p.projectStatus}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-mid)', paddingLeft: 16 }}>
            {p.accountName}{p.projectManager ? ` · PM: ${p.projectManager}` : ''}
          </div>
        </div>

        <div style={{ flex: 1.6, minWidth: 170 }}>
          <ProgressRow label="Schedule" pct={p.scheduleProgress?.pct ?? null} color="var(--primary-light)" emptyText="No schedule" />
          <ProgressRow label="Milestones" pct={p.milestoneProgress?.pct ?? null} color="var(--primary-light)" emptyText="None set" />
          <div title={p.effort?.pct !== null && p.effort ? `${Math.round(p.effort.actualHours)}h of ${Math.round(p.effort.plannedHours)}h budgeted` : undefined}>
            <ProgressRow
              label="Effort"
              pct={p.effort?.pct ?? null}
              color={effortColor(p.effort?.pct ?? null)}
              emptyText={p.effort?.actualHours ? `${Math.round(p.effort.actualHours)}h, not budgeted` : 'Not budgeted'}
            />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 2 }}>Next milestone</div>
          {p.nextMilestone ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dark)' }}>{p.nextMilestone.title}</div>
              <div style={{ fontSize: 11, color: 'var(--primary-light)' }}>{fmtFullDate(p.nextMilestone.targetDate)}</div>
            </>
          ) : <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>—</div>}
        </div>

        <div style={{ flex: 0.7, minWidth: 80, textAlign: 'center' }} title={isPostGoLive && p.goLive?.date ? `Went live ${fmtFullDate(p.goLive.date)}` : undefined}>
          <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 2 }}>Go-live</div>
          {isPostGoLive ? (
            <div style={{ fontSize: 16, fontWeight: 'bold', fontFamily: 'Georgia', color: 'var(--green)' }}>Live</div>
          ) : (
            <div style={{ fontSize: 16, fontWeight: 'bold', fontFamily: 'Georgia', color: days !== null && days < 0 ? 'var(--red)' : 'var(--heading)' }}>
              {goLiveMark}{fmtCountdown(days)}
            </div>
          )}
        </div>

        <div style={{ flex: 0.6, minWidth: 90, textAlign: 'center' }}>
          {p.openRiskCount > 0 ? (
            <span className="badge badge-red">⚠ {p.openRiskCount} open</span>
          ) : (
            <span className="badge badge-green">✓ Clear</span>
          )}
        </div>

        <div style={{ color: 'var(--text-mid)', fontSize: 12 }}>{expanded ? '▲' : '▼'}</div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }} onClick={(e) => e.stopPropagation()}>
          <div className="grid-3" style={{ gap: 16 }}>
            <div>
              <div style={SECTION_LABEL_STYLE}>Health</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {HEALTH_DIMENSIONS.map(([field, label]) => (
                  <span key={field} className={`badge ${RAG_CONFIG[ragKey(p[field])].badgeClass}`}>
                    {label}: {p[field] || 'Not rated'}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div style={SECTION_LABEL_STYLE}>Go-Live</div>
              {p.goLive?.date ? (
                <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 8 }}>
                  {isPostGoLive ? 'Went live ' : 'Targeting '}{fmtFullDate(p.goLive.date)}
                  {p.goLive.title ? ` — ${p.goLive.title}` : ''}
                  {p.goLive.source === 'targetEndDate' && ' (project target date; no go-live milestone set)'}
                  {p.goLive.source === 'scheduleEnd' && ' (inferred from the last dated schedule row)'}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 8 }}>No go-live date set yet.</div>
              )}
              {overdue.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {overdue.map((m) => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
                      <span style={{ color: 'var(--text-dark)' }}>{m.isGoLive ? '🚀 ' : ''}{m.title}</span>
                      <span style={{ color: 'var(--red)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {fmtCountdown(daysUntil(m.targetDate))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : p.nextMilestone ? (
                <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  No milestones overdue. Next up: <strong>{p.nextMilestone.title}</strong> ({fmtFullDate(p.nextMilestone.targetDate)})
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>No milestones recorded.</div>
              )}
            </div>
            <div>
              <div style={SECTION_LABEL_STYLE}>Quick links</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                <Link className="btn btn-outline" style={QUICK_LINK_STYLE} to={projectPath('/project-launch', p.id)}>Project Details →</Link>
                {SCHEDULING_ENABLED && <Link className="btn btn-outline" style={QUICK_LINK_STYLE} to={projectPath('/schedule', p.id)}>Project Schedule →</Link>}
                <Link className="btn btn-outline" style={QUICK_LINK_STYLE} to={projectPath('/registers', p.id, { tab: 'raaidd' })}>Risk Register →</Link>
                <Link className="btn btn-outline" style={QUICK_LINK_STYLE} to={projectPath('/calendar', p.id)}>Project Calendar →</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [ragFilter, setRagFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/dashboard/portfolio-summary')
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div>
        <div className="page-header">
          <div className="page-eyebrow">Professional Services · Portfolio</div>
          <h1 className="page-title">Dashboard</h1>
        </div>
        <div className="card" style={{ borderLeft: '4px solid var(--red)', color: 'var(--red)' }}>Couldn't load the portfolio: {error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="page-header">
          <div className="page-eyebrow">Professional Services · Portfolio</div>
          <h1 className="page-title">Dashboard</h1>
        </div>
        <div className="card" style={{ color: 'var(--text-mid)' }}>Loading portfolio…</div>
      </div>
    );
  }

  const { projects, upcoming, summary } = data;
  const sorted = [...projects].sort((a, b) => {
    const ra = RAG_ORDER[ragKey(a.ragStatus)];
    const rb = RAG_ORDER[ragKey(b.ragStatus)];
    if (ra !== rb) return ra - rb;
    const da = daysUntil(a.goLive?.date);
    const db = daysUntil(b.goLive?.date);
    return (da ?? Infinity) - (db ?? Infinity);
  });
  const filtered = ragFilter === 'all' ? sorted : sorted.filter((p) => ragKey(p.ragStatus) === ragFilter);
  const filterChips = ['all', 'Red', 'Amber', 'Green', ...(summary.ragCounts.Unset ? ['Unset'] : [])];

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Portfolio</div>
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          &nbsp;·&nbsp; {summary.totalProjects} active project{summary.totalProjects === 1 ? '' : 's'}
        </p>
      </div>

      <div className="stat-row">
        <Stat value={summary.ragCounts.Green} label="On Track" />
        <Stat value={summary.ragCounts.Amber} label="At Risk" tone={summary.ragCounts.Amber ? 'warn' : ''} />
        <Stat value={summary.ragCounts.Red} label="Critical" tone={summary.ragCounts.Red ? 'alert' : ''} />
        <Stat
          value={summary.openRiskItems}
          label="Open Risks/Issues"
          tone={summary.openRiskItems ? 'warn' : ''}
          sub={summary.projectsWithOpenRisks ? `across ${summary.projectsWithOpenRisks} project${summary.projectsWithOpenRisks === 1 ? '' : 's'}` : undefined}
        />
        <Stat value={summary.overdueMilestoneProjects} label="Projects w/ Overdue Milestones" tone={summary.overdueMilestoneProjects ? 'alert' : ''} />
      </div>
      <RagMixBar counts={summary.ragCounts} total={summary.totalProjects} />

      <div style={{ display: 'flex', gap: 24, marginTop: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: 3, minWidth: 420 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {filterChips.map((f) => (
              <button
                key={f}
                onClick={() => setRagFilter(f)}
                style={{
                  padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)',
                  background: ragFilter === f ? 'var(--primary-dark)' : 'var(--surface)',
                  color: ragFilter === f ? 'var(--white)' : 'var(--text-mid)',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {f === 'all' ? 'All Projects' : RAG_CONFIG[f].label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="card placeholder-page">No projects match this filter.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filtered.map((p) => (
                <ProjectRow key={p.id} project={p} expanded={expandedId === p.id} onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)} />
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 280 }}>
          <div className="card">
            <div className="card-title">Upcoming Milestones</div>
            {upcoming.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>No milestones due across the portfolio in the next 45 days.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                {upcoming.map((e) => (
                  <Link key={e.id} to={eventLink(e)} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', textDecoration: 'none' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.isGoLive ? 'var(--violet)' : 'var(--primary-dark)', marginTop: 5, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dark)' }}>
                        {e.isGoLive ? '🚀 ' : ''}{e.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-mid)' }}>{e.projectName} · {fmtEventDate(e)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
